import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { WORKFLOW_DISPLAY_DEFAULTS } from "../../../../constants/workflowDisplayDefaults";
import DecoratorDestinationRulesEditor from './DecoratorDestinationRulesEditor';

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
const DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES = [
  "Shipped",
  "Partially Received",
];

const authConfig = () => ({
  headers: {
    accessToken: localStorage.getItem("accessToken"),
  },
});

const getUserLabel = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  if (fullName && user?.email) return `${fullName} (${user.email})`;
  return fullName || user?.email || `User ${user?.id || ""}`.trim();
};

export default function InternalShippingSettingsPanel({ instanceUniqueId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shippingReps, setShippingReps] = useState([]);
  const [selectedShippingReps, setSelectedShippingReps] = useState([]);
  const [selectedReceivingStatuses, setSelectedReceivingStatuses] = useState([]);
  const [notifyPurchasingRepOnReceivingNote, setNotifyPurchasingRepOnReceivingNote] = useState(false);
  const [decorators, setDecorators] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [decoratorDestinationRules, setDecoratorDestinationRules] = useState({});
  const [decoratorRulesDirty, setDecoratorRulesDirty] = useState(false);
  const [loadError, setLoadError] = useState('');
  const settingsRequestRef = useRef(0);

  const poStatusOptions = useMemo(
    () => WORKFLOW_DISPLAY_DEFAULTS.PO.map((status) => ({
      value: status.statusKey,
      label: status.displayLabel || status.statusKey,
    })),
    []
  );

  const loadSettings = useCallback(async () => {
    if (!instanceUniqueId) return;
    const requestId = ++settingsRequestRef.current;
    try {
      setLoading(true);
      setLoadError('');
      const response = await axios.get(
        `${BASE_URL}/instances/${instanceUniqueId}/internal-shipping-settings`,
        authConfig()
      );
      if (requestId !== settingsRequestRef.current) return;
      const data = response.data || {};
      setDecorators(Array.isArray(data.decorators) ? data.decorators : []);
      setCompanyAddresses(Array.isArray(data.companyAddresses) ? data.companyAddresses : []);
      setDecoratorDestinationRules(data?.settings?.decoratorDestinationRules || {});
      setDecoratorRulesDirty(false);
      setShippingReps(Array.isArray(data.shippingReps) ? data.shippingReps : []);
      setSelectedShippingReps(Array.isArray(data.selectedShippingReps) ? data.selectedShippingReps : []);
      setSelectedReceivingStatuses(
        Array.isArray(data?.settings?.receivingEligiblePoStatuses)
          ? data.settings.receivingEligiblePoStatuses
          : DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES
      );
      setNotifyPurchasingRepOnReceivingNote(
        data?.settings?.notifyPurchasingRepOnReceivingNote === true
      );
    } catch (error) {
      if (requestId !== settingsRequestRef.current) return;
      const message = error?.response?.data?.message || "Could not load internal shipping settings.";
      setLoadError(message);
      toast.error(message);
    } finally {
      if (requestId === settingsRequestRef.current) setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadSettings();
    return () => { settingsRequestRef.current += 1; };
  }, [loadSettings]);

  const selectedIds = useMemo(
    () => selectedShippingReps
      .map((user) => Number(user.id))
      .filter((id) => Number.isInteger(id) && id > 0),
    [selectedShippingReps]
  );

  const saveSettings = async () => {
    const requestId = settingsRequestRef.current;
    try {
      setSaving(true);
      const response = await axios.put(
        `${BASE_URL}/instances/${instanceUniqueId}/internal-shipping-settings`,
        {
          shippingUpdateRecipientUserIds: selectedIds,
          receivingEligiblePoStatuses: selectedReceivingStatuses,
          notifyPurchasingRepOnReceivingNote: Boolean(notifyPurchasingRepOnReceivingNote),
          ...(decoratorRulesDirty ? { decoratorDestinationRules } : {}),
        },
        authConfig()
      );
      if (requestId !== settingsRequestRef.current) return;
      const data = response.data || {};
      setDecorators(Array.isArray(data.decorators) ? data.decorators : []);
      setCompanyAddresses(Array.isArray(data.companyAddresses) ? data.companyAddresses : []);
      setDecoratorDestinationRules(data?.settings?.decoratorDestinationRules || {});
      setDecoratorRulesDirty(false);
      setShippingReps(Array.isArray(data.shippingReps) ? data.shippingReps : []);
      setSelectedShippingReps(Array.isArray(data.selectedShippingReps) ? data.selectedShippingReps : []);
      setSelectedReceivingStatuses(
        Array.isArray(data?.settings?.receivingEligiblePoStatuses)
          ? data.settings.receivingEligiblePoStatuses
          : DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES
      );
      setNotifyPurchasingRepOnReceivingNote(
        data?.settings?.notifyPurchasingRepOnReceivingNote === true
      );
      toast.success("Internal shipping settings saved.");
    } catch (error) {
      if (requestId === settingsRequestRef.current) {
        toast.error(error?.response?.data?.message || "Could not save internal shipping settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (loadError) {
    return <Alert severity="error" action={<Button onClick={loadSettings}>Retry</Button>}>{loadError}</Alert>;
  }

  return (
    <Card variant="outlined" sx={{ borderColor: "divider", boxShadow: "none" }}>
      <CardContent>
        <Stack spacing={3}>
          <DecoratorDestinationRulesEditor
            key={instanceUniqueId}
            decorators={decorators}
            companyAddresses={companyAddresses}
            rules={decoratorDestinationRules}
            onChange={(rules) => { setDecoratorDestinationRules(rules); setDecoratorRulesDirty(true); }}
            disabled={saving}
          />
          <Stack spacing={2}>
            <div>
              <Typography variant="subtitle1" fontWeight={600}>
                Shipping Update Recipients
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select which internal shipping reps should receive PO shipping update emails for this instance.
              </Typography>
            </div>

            {!shippingReps.length ? (
              <Alert severity="info">
                No shipping reps found. Add users with the Shipping Rep role first.
              </Alert>
            ) : (
              <Autocomplete
                multiple
                options={shippingReps}
                value={selectedShippingReps}
                onChange={(_event, nextValue) => setSelectedShippingReps(nextValue)}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                getOptionLabel={getUserLabel}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Internal shipping contacts"
                    placeholder="Select shipping reps"
                  />
                )}
              />
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Receiving Notifications
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={notifyPurchasingRepOnReceivingNote}
                  onChange={(event) =>
                    setNotifyPurchasingRepOnReceivingNote(event.target.checked)
                  }
                />
              )}
              label="Email Purchasing Rep when Receiving Notes are added"
            />
            <FormHelperText sx={{ mt: "0 !important" }}>
              When enabled, saving Additional Notes while receiving goods will email the purchasing representative assigned to the project.
            </FormHelperText>
          </Stack>

          <Stack spacing={2}>
            <div>
              <Typography variant="subtitle1" fontWeight={600}>
                Purchase Order Receiving Statuses
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select which purchase-order statuses can appear and be received on the Shipping Receiving page. Shipped and Partially Received are enabled by default.
              </Typography>
            </div>

            <Autocomplete
              multiple
              options={poStatusOptions}
              value={poStatusOptions.filter((option) => selectedReceivingStatuses.includes(option.value))}
              onChange={(_event, nextValue) =>
                setSelectedReceivingStatuses(nextValue.map((option) => option.value))
              }
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || option?.value || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PO statuses available for receiving"
                  placeholder="Select PO statuses"
                  helperText="Only purchase orders with one of these statuses will appear in Receiving. Shipped and Partially Received are enabled by default."
                />
              )}
            />
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" onClick={saveSettings} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
